import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAllTickets, deleteTicket } from '../services/TicketDeCaisseService';
import { useTranslation } from 'react-i18next';
import PreviewDialog from '../../pos/components/PreviewDialog';
import { getThermalReceiptTemplate } from '../../pos/components/ThermalReceiptTemplate';

const TicketDeCaissePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get store info from Redux store
  const storeInfo = useSelector((state) => state.company?.companyInfo || state.auth?.companyInfo);

  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Preview Dialog State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [currentTicketForPrint, setCurrentTicketForPrint] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [searchTerm, tickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching all tickets...');
      
      const response = await getAllTickets();
      
      // Handle different response structures
      let ticketsData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        ticketsData = response.payload;
      } else if (response?.data?.payload && Array.isArray(response.data.payload)) {
        ticketsData = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        ticketsData = response.data;
      } else if (Array.isArray(response)) {
        ticketsData = response;
      }
      
      console.log('✅ Fetched tickets:', ticketsData.length);
      console.log('📦 Sample ticket data:', ticketsData[0]);
      setTickets(ticketsData);
      setFilteredTickets(ticketsData);
    } catch (error) {
      console.error('❌ Error fetching tickets:', error);
      setError(error.message || 'Failed to load tickets');
      toast.error(t('failed_to_load_tickets') || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    if (!searchTerm.trim()) {
      setFilteredTickets(tickets);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = tickets.filter((ticket) => {
      return (
        ticket.TicketNumber?.toLowerCase().includes(searchLower) ||
        ticket.CustomerName?.toLowerCase().includes(searchLower) ||
        ticket.PaymentMethod?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredTickets(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateTicket = () => {
    navigate('/tickets/add');
  };

  const handleViewTicket = (ticket) => {
    try {
      console.log('👁️ Viewing ticket:', ticket);
      
      // Use already loaded ticket data
      generateReceiptPreview(ticket);
    } catch (error) {
      console.error('❌ Error viewing ticket:', error);
      toast.error(t('failed_to_load_ticket') || 'Failed to load ticket details');
    }
  };

  const handleEditTicket = (ticketId) => {
    navigate(`/tickets/edit/${ticketId}`);
  };

  const handleDeleteTicket = async (ticketId, ticketNumber) => {
    if (window.confirm(t('confirm_delete_ticket', { ticket: ticketNumber }) || `Are you sure you want to delete ticket ${ticketNumber}?`)) {
      try {
        await deleteTicket(ticketId);
        toast.success(t('ticket_deleted_success') || 'Ticket deleted successfully');
        fetchTickets();
      } catch (error) {
        toast.error(error.response?.data?.message || t('failed_to_delete_ticket') || 'Failed to delete ticket');
      }
    }
  };

  const handlePrintTicket = (ticket) => {
    try {
      console.log('🖨️ Printing ticket:', ticket);
      
      // Use already loaded ticket data
      generateReceiptPreview(ticket);
    } catch (error) {
      console.error('❌ Error printing ticket:', error);
      toast.error(t('failed_to_print_ticket') || 'Failed to print ticket');
    }
  };

  const generateReceiptPreview = (ticket) => {
    console.log('📦 Generating receipt preview for ticket:', ticket);
    console.log('🏢 Using company info from store:', companyInfo);

    // Prepare cart data from ticket line items - map to match POS format
    const cart = (ticket.LineItems || ticket.Items || []).map(item => ({
      Id: item.ProductId || item.Id,
      Name: item.ProductName || item.Name || 'Product',
      Price: parseFloat(item.UnitPriceExclTax || item.UnitPrice || item.Price || 0), // Base price (excl VAT)
      Quantity: parseFloat(item.Quantity || 1),
      VAT: parseFloat(item.VATRate || item.VAT || ticket.VATPercentage || 21),
      PriceInclVAT: parseFloat(item.UnitPriceInclVat || item.UnitPriceInclVAT || 0),
      // Calculate total for each line item
      Total: parseFloat(item.TotalAmountInclVATAfterDiscount || item.TotalAmountInclVAT || item.TotalPrice || (item.Quantity * (item.UnitPriceInclVat || item.UnitPrice)) || 0)
    }));

    console.log('🛒 Mapped cart items:', cart);

    // Prepare customer data
    const customer = ticket.Customer || (ticket.CustomerId ? { 
      Id: ticket.CustomerId,
      Name: ticket.CustomerName || 'Walk-in Customer' 
    } : null);
    
    // Use store info from Redux store, then ticket's company info, then fallback
    const company = storeInfo || ticket.CompanyInfo || {
      CompanyName: 'Your Store',
      Street: 'Street Address',
      City: 'City',
      PostCode: 'Postal Code',
      PhoneNumber: 'Phone',
      BTWNumber: 'VAT Number',
    };
    
    const paymentMethod = ticket.PaymentMethod || 'CASH';
    const paidAmount = parseFloat(ticket.PaidAmount || ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmount || 0);
    const receiptNumber = ticket.ReceiptNumber || ticket.TicketNumber;
    
    // Map discount data if exists
    const discount = ticket.DiscountAmount && ticket.DiscountAmount > 0 ? {
      type: ticket.DiscountType?.toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
      value: ticket.DiscountType?.toLowerCase() === 'percentage' 
        ? parseFloat(ticket.DiscountPercentage || 0) 
        : parseFloat(ticket.DiscountAmount || 0),
      amount: parseFloat(ticket.DiscountAmount || 0),
      reason: ticket.DiscountReason || '',
    } : null;
    
    const customVATRate = ticket.VATPercentage !== null && ticket.VATPercentage !== undefined 
      ? parseFloat(ticket.VATPercentage) 
      : null;

    console.log('🛒 Cart items:', cart.length);
    console.log('👤 Customer:', customer);
    console.log('💰 Payment:', paymentMethod, paidAmount);
    console.log('🎁 Discount:', discount);
    console.log('📊 Custom VAT Rate:', customVATRate);

    // Validate cart data
    if (!cart || cart.length === 0) {
      console.warn('⚠️ No line items found in ticket');
      toast.warning(t('no_items_in_ticket') || 'No items found in this ticket');
      return;
    }

    // Generate receipt HTML
    const receiptHtml = getThermalReceiptTemplate(
      cart,
      customer,
      company,
      paymentMethod,
      paidAmount,
      receiptNumber,
      discount,
      customVATRate
    );

    setPreviewHtml(receiptHtml);
    setCurrentTicketForPrint(ticket);
    setPreviewOpen(true);
  };

  const handlePrint = () => {
    try {
      console.log('🖨️ Printing receipt...');
      
      // Create a hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow || printFrame.contentDocument;
      if (printDocument.document) {
        printDocument.document.open();
        printDocument.document.write(previewHtml);
        printDocument.document.close();
      }
      
      // Wait for content to load then print
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Remove iframe after printing
        setTimeout(() => {
          document.body.removeChild(printFrame);
          setPreviewOpen(false);
          toast.success(t('print_sent') || 'Print job sent successfully');
        }, 100);
      }, 250);
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print receipt');
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewHtml('');
    setCurrentTicketForPrint(null);
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      CASH: 'success',
      BANCONTACT: 'primary',
      VISA: 'info',
      MASTERCARD: 'warning',
      CHEQUE: 'secondary',
      VIREMENT: 'default',
      PREPAID: 'info',
    };
    return colors[method?.toUpperCase()] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'success',
      'Pending': 'warning',
      'Cancelled': 'error',
      'Refunded': 'info',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Calculate total amount from line items
  const calculateTicketTotal = (ticket) => {
    if (ticket.TotalAmountInclVAT !== null && ticket.TotalAmountInclVAT !== undefined) {
      return parseFloat(ticket.TotalAmountInclVAT);
    }
    if (ticket.TotalAmount !== null && ticket.TotalAmount !== undefined) {
      return parseFloat(ticket.TotalAmount);
    }
    // Calculate from line items if not available
    const lineItems = ticket.LineItems || ticket.Items || [];
    return lineItems.reduce((sum, item) => {
      const itemTotal = parseFloat(item.TotalPrice || (item.Quantity * item.UnitPrice) || 0);
      return sum + itemTotal;
    }, 0);
  };

  // Get line items count
  const getLineItemsCount = (ticket) => {
    const lineItems = ticket.LineItems || ticket.Items || [];
    return lineItems.length;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('tickets_de_caisse') || 'Tickets de Caisse'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('manage_all_tickets') || 'Manage all your tickets de caisse'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTickets}
            sx={{ borderRadius: 2 }}
          >
            {t('refresh') || 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTicket}
            sx={{ borderRadius: 2 }}
          >
            {t('create_ticket') || 'Create Ticket'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <Typography variant="caption" color="text.secondary">
              {t('total_tickets') || 'Total Tickets'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {tickets.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
            <Typography variant="caption" color="text.secondary">
              {t('total_revenue') || 'Total Revenue'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              {formatCurrency(tickets.reduce((sum, ticket) => sum + calculateTicketTotal(ticket), 0))}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <Typography variant="caption" color="text.secondary">
              {t('today_tickets') || "Today's Tickets"}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {tickets.filter(ticket => {
                const ticketDate = new Date(ticket.CreatedDate || ticket.Date);
                const today = new Date();
                return ticketDate.toDateString() === today.toDateString();
              }).length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, bgcolor: '#f3e5f5', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
            <Typography variant="caption" color="text.secondary">
              {t('avg_ticket_value') || 'Avg Ticket Value'}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {formatCurrency(tickets.length > 0 ? tickets.reduce((sum, ticket) => sum + calculateTicketTotal(ticket), 0) / tickets.length : 0)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('search_tickets') || 'Search by ticket number, customer name, payment method...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Paper>

      {/* Tickets Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('ticket_number') || 'Ticket #'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('no_line_items') || 'No. Line Items'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('date_time') || 'Date & Time'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('customer') || 'Customer'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('payment_method') || 'Payment Method'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                  {t('amount') || 'Amount'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('status') || 'Status'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="center">
                  {t('actions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? t('no_tickets_found') || 'No tickets found matching your search'
                        : t('no_tickets_yet') || 'No tickets de caisse yet. Create your first ticket!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ticket) => (
                    <TableRow
                      key={ticket.Id}
                      sx={{
                        '&:hover': { bgcolor: '#f9f9f9' },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {ticket.TicketNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getLineItemsCount(ticket)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(ticket.CreatedDate || ticket.Date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(ticket.CreatedDate || ticket.Date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ticket.CustomerName || t('walk_in_customer') || 'Walk-in Customer'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.PaymentMethod || 'Cash'}
                          size="small"
                          color={getPaymentMethodColor(ticket.PaymentMethod)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(calculateTicketTotal(ticket))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.Status || 'Completed'}
                          size="small"
                          color={getStatusColor(ticket.Status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('view') || 'View'}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('print') || 'Print'}>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handlePrintTicket(ticket)}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('edit') || 'Edit'}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleEditTicket(ticket.Id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('delete') || 'Delete'}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTicket(ticket.Id, ticket.TicketNumber)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTickets.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Preview Dialog */}
      <PreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        htmlContent={previewHtml}
        onPrint={handlePrint}
        format="receipt"
      />
    </Container>
  );
};

export default TicketDeCaissePage;